import styled, { css } from 'styled-components';

import { Div, StyledUtilsProps } from '../helpers/StyledUtils';

interface Props extends StyledUtilsProps {
  raised?: true | 'button' | number;
  muted?: boolean;
  primary?: boolean;
  borderless?: boolean;
  danger?: boolean;
  warning?: boolean;
  dashed?: boolean;
  overflow_hidden?: true;
  separator?: boolean;
}

export const StyledCardSection = styled(Div)<{
  primary?: boolean;
  muted?: boolean;
  borderless?: boolean;
  danger?: boolean;
  warning?: boolean;
  highlight?: boolean;
  scroll?: boolean;
}>(
  ({ theme, muted, borderless = false, danger, warning, highlight, scroll, primary }) => css`
    max-width: 100%;

    ${borderless &&
    css`
      border: none !important;
    `}

    ${scroll &&
    css`
      overflow-y: scroll;
      &::-webkit-scrollbar {
        display: none;
      }
    `}

    ${primary &&
    css`
      background-color: ${theme.colors.surface.container.primary};
    `};
    ${muted &&
    css`
      background-color: ${theme.colors.surface.base.variant_surface_3};
    `};

    ${danger &&
    css`
      background-color: ${theme.colors.surface.container.danger};
    `};
    ${warning &&
    css`
      background-color: ${theme.colors.surface.container.warning};
    `};
    ${highlight &&
    css`
      &:last-of-type {
        border-radius: 0 0 ${theme.radius.normal_inset} ${theme.radius.normal_inset};
      }
      background-color: ${theme.colors.surface.base.background};
    `};
  `,
);

export const StyledCard = styled(Div)<Props>(({
  theme,
  raised,
  muted,
  primary,
  borderless = false,
  danger,
  warning,
  dashed = false,
  overflow_hidden,
  separator = true,
}) => {
  const shadow =
    raised === 'button'
      ? theme.shadows.neutral.base
      : raised
        ? `${theme.elevation[raised === true ? 1 : raised]}`
        : null;
  return css`
    width: 100%;
    text-align: left;
    box-sizing: border-box;
    background-color: ${muted
      ? theme.colors.surface.base.background
      : theme.colors.surface.base.surface};
    border-radius: ${theme.radius.normal};
    border: ${(muted && !dashed) || borderless || shadow
      ? 'none'
      : dashed
        ? theme.border_dashed
        : theme.border};
    ${shadow &&
    css`
      box-shadow: ${shadow};
    `}
    ${overflow_hidden &&
    css`
      overflow: hidden;
    `};
    ${StyledCardSection}:not(:first-child) {
      border-top: ${separator ? theme.border : 'none'};
    }
    /* Round the outer sections so filled ones (e.g. muted table headers) follow the card corners. */
    > ${StyledCardSection}:first-child {
      border-top-left-radius: ${theme.radius.normal_inset};
      border-top-right-radius: ${theme.radius.normal_inset};
    }
    > ${StyledCardSection}:last-child {
      border-bottom-left-radius: ${theme.radius.normal_inset};
      border-bottom-right-radius: ${theme.radius.normal_inset};
    }

    ${primary &&
    css`
      border: 1px solid ${theme.colors.outline.primary};
    `};

    ${danger &&
    css`
      border: 1px solid ${theme.colors.outline.danger};
      ${StyledCardSection}:last-child {
        border-top: 1px solid ${theme.colors.outline.danger};
      }
    `}

    ${warning &&
    css`
      border: 1px solid ${theme.colors.outline.warning};
      > ${StyledCardSection}:last-child {
        border-top: 1px solid ${theme.colors.outline.warning};
      }
    `}
  `;
});
